/**
 * The marking, as the profile reports it — `05-reporting-marking.md` §B2/§B3, read-only.
 *
 * **One marking per official, for the whole tournament** (ruling R1′): one set of bars, one
 * Performance Outcome, one status, whichever competitions the official worked. This panel
 * says where that one marking stands; the bars themselves are on the marking screen, one
 * click away, and are deliberately not repeated here — two places to move the same mark is
 * two places for it to be moved by accident.
 *
 * Three rules it keeps, all §B3's:
 *
 *  1. **The set is the official's own and nothing is filtered out of it.** The criteria
 *     arrive from the API in TMS render order, zero-weight rows included: `Video Umpire` is
 *     recorded and printed, and drops out of the outcome by arithmetic rather than by being
 *     dropped from a table.
 *  2. **`N/A` leaves the calculation, not the list.** A criterion marked `N/A` says so and
 *     its weight comes off the live Σ, which is what the foot prints rather than the
 *     constant 200.
 *  3. **Unmarked is not zero.** A criterion with no mark shows `—`, and the panel counts how
 *     many are still to do — the same list a push is refused on.
 */

import { tidyCriterionDisplayName } from "@hockeytd/shared";
import { Link } from "react-router-dom";

import type { MarkingPanelBody } from "../../api/types.js";
import { markFor } from "../marking/outcome.js";

export function MarksSummaryPanel({
  panel,
  markingPath,
}: {
  panel: MarkingPanelBody;
  /** The screen where the bars actually move — this panel only reports them. */
  markingPath: string;
}): React.JSX.Element {
  const marked = panel.criteria.filter((criterion) => {
    const mark = markFor(panel.marks, criterion.criterion_stable_key);
    return mark !== undefined && (mark.na || mark.value !== null);
  }).length;

  return (
    <section className="panel panel--d1" aria-labelledby="marksTitle" data-testid="marks-summary" data-howto="marks-summary">
      <div className="panel__head">
        <h3 className="panel__title" id="marksTitle">
          Marking
        </h3>
        <span className="panel__note">Given Once, At The End Of The Tournament</span>
        <span className="spacer" />
        <span className="readonly-tag">Read-Only</span>
        <Link className="linkbtn-ext" to={markingPath}>
          Marking Panel
        </Link>
      </div>

      <table className="mtable sumtable" data-testid="marks-table">
        <thead>
          <tr>
            <th scope="col">Criterion</th>
            <th scope="col">Weight</th>
            <th scope="col">Mark</th>
          </tr>
        </thead>
        <tbody>
          {panel.criteria.map((criterion) => {
            const mark = markFor(panel.marks, criterion.criterion_stable_key);
            return (
              <tr
                key={criterion.criterion_stable_key}
                data-testid={`mark-${criterion.criterion_stable_key}`}
              >
                <td>
                  {tidyCriterionDisplayName(criterion.name)}
                  {criterion.weight === 0 ? (
                    <span
                      className="zeroweight"
                      title="Recorded and printed on the report, but weight 0 — it does not move the Performance Outcome"
                    >
                      Not Weighted
                    </span>
                  ) : null}
                </td>
                <td className="num" title="Weight">
                  {criterion.weight}
                </td>
                <td className="num">
                  {mark === undefined ? (
                    <span className="unmarked" title="Not marked yet">
                      —
                    </span>
                  ) : mark.na ? (
                    <span className="unmarked" title="Out of the calculation">
                      N/A
                    </span>
                  ) : (
                    <>
                      {mark.value}
                      <span className="of">/100</span>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="panel__foot">
        <p className="panel__count num" data-testid="marks-count">
          {marked} Of {panel.criteria.length} Criteria Marked
        </p>
        <p>
          <b>Weights total {panel.weightTotal} —</b> the Performance Outcome is the weighted
          mean of the marks, and the live Σ here is <b>{panel.liveWeightTotal}</b> once every
          criterion marked <span className="num">N/A</span> is left out of the total altogether.
          Marks step in tens from 0 to 100.
        </p>
        {panel.unmarkedCriteria.length === 0 ? null : (
          <p data-testid="marks-outstanding">
            <b>Still to mark —</b> {panel.unmarkedCriteria.join(", ")}. A push of the marks is
            refused while any remain.
          </p>
        )}
      </div>
    </section>
  );
}
